"use client";

import { useEffect, useRef, useState } from "react";

const testimonials = [
  {
    quote: "I\'ve been through brand after brand — all of them. Kratapillar is the first brand where I ordered a second kilo without even finishing the first. Consistency is real.",
    name: "Marcus T.",
    detail: "Early Tester · Green Line",
    initials: "MT",
  },
  {
    quote: "The lab transparency alone sold me. I could actually read the COA without needing a chemistry degree. The Green Line is exactly what I needed to replace three different products from my old rotation.",
    name: "Danielle R.",
    detail: "Early Tester · Green Line + Red Line",
    initials: "DR",
  },
  {
    quote: "White Line hits exactly the same every single time. That\'s all I wanted. Done chasing the perfect batch.",
    name: "James K.",
    detail: "Early Tester · White Line",
    initials: "JK",
  },
  {
    quote: "The free sample was a nice touch — I ended up switching from Green to Red based on it. Smart move by Kratapillar. The 30-day guarantee sealed the deal on my first order.",
    name: "Priya S.",
    detail: "Early Tester · Red Line",
    initials: "PS",
  },
];

export default function ReviewsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const refs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1 }
    );
    refs.current.forEach((r) => r && observer.observe(r));
    return () => observer.disconnect();
  }, []);

  const active = testimonials[activeIndex];

  return (
    <section className="py-28" style={{ backgroundColor: "#F6F5F2" }}>
      <div className="max-w-container mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          ref={(el) => { refs.current[0] = el; }}
          className="reveal mb-16"
        >
          <span className="section-label mb-4 block">Social Proof</span>
          <h2
            className="font-serif font-bold"
            style={{
              fontFamily: "Fraunces, Georgia, serif",
              fontSize: "clamp(2rem, 3.5vw, 3rem)",
              letterSpacing: "-0.025em",
              lineHeight: "1.1",
              color: "#1F1F1F",
            }}
          >
            From People Who
            <br />
            <em style={{ color: "#3F5A45" }}>Already Switched.</em>
          </h2>
        </div>

        {/* Main testimonial */}
        <div
          ref={(el) => { refs.current[1] = el; }}
          className="reveal grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-10"
        >
          {/* Quote */}
          <div className="lg:col-span-8">
            <div
              className="p-10 lg:p-14 relative"
              style={{ backgroundColor: "white", border: "1px solid #E0DFDB" }}
            >
              {/* Quote mark */}
              <div
                className="font-serif text-8xl leading-none mb-4"
                style={{
                  fontFamily: "Fraunces, Georgia, serif",
                  color: "#3F5A45",
                  opacity: 0.12,
                  lineHeight: "0.6",
                }}
              >
                "
              </div>
              <blockquote
                className="font-serif text-xl lg:text-2xl leading-relaxed mb-8 relative z-10"
                style={{
                  fontFamily: "Fraunces, Georgia, serif",
                  color: "#1F1F1F",
                  fontStyle: "italic",
                  letterSpacing: "-0.01em",
                  lineHeight: "1.55",
                }}
              >
                {active.quote}
              </blockquote>

              <div className="flex items-center gap-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                  style={{ backgroundColor: "#3F5A45", fontFamily: "Inter, system-ui, sans-serif" }}
                >
                  {active.initials}
                </div>
                <div>
                  <div
                    className="font-semibold text-sm"
                    style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}
                  >
                    {active.name}
                  </div>
                  <div
                    className="text-xs"
                    style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                  >
                    {active.detail}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Selector */}
          <div className="lg:col-span-4 space-y-3">
            {testimonials.map((t, i) => (
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                className="w-full text-left p-4 transition-all duration-200"
                style={{
                  backgroundColor: activeIndex === i ? "white" : "transparent",
                  border: `1px solid ${activeIndex === i ? "#3F5A45" : "#E0DFDB"}`,
                  borderRadius: "2px",
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                    style={{
                      backgroundColor: activeIndex === i ? "#3F5A45" : "#E0DFDB",
                      color: activeIndex === i ? "white" : "#6B6B6B",
                      fontFamily: "Inter, system-ui, sans-serif",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {t.initials}
                  </div>
                  <div>
                    <div
                      className="text-xs font-semibold"
                      style={{
                        color: activeIndex === i ? "#1F1F1F" : "#6B6B6B",
                        fontFamily: "Inter, system-ui, sans-serif",
                      }}
                    >
                      {t.name}
                    </div>
                    <div
                      className="text-xs"
                      style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                    >
                      {t.detail}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Rating summary */}
        <div
          ref={(el) => { refs.current[2] = el; }}
          className="reveal flex flex-wrap items-center gap-8"
        >
          <div className="flex items-center gap-3">
            <div
              className="font-serif text-4xl font-bold"
              style={{ fontFamily: "Fraunces, Georgia, serif", color: "#1F1F1F", letterSpacing: "-0.03em" }}
            >
              4.9
            </div>
            <div>
              <div className="flex gap-0.5 mb-1">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="#3F5A45">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
              <div
                className="text-xs"
                style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
              >
                From our early testing community
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}